import React, { useCallback, useMemo, useState } from "react";
import { Table, Button, Tag, Icon, Modal, Spin, Checkbox, Tooltip, Badge, Result, Skeleton } from "antd";
import { Provider } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { store } from "../../../../redux";
import ScreenContainer from "../../../../newSrc/shared/containers/ScreenContainer";
import NumberFormat from "../../../NumberFormat";
import CircuitButton from "../../../customComponents/CircuitButton/CircuitButton";
import MultipleFacesButton from "../../../customComponents/MultipleFacesButton/MultipleFacesButton";
import SyncCircuitAlert from "../../../shared/SyncCircuit/SyncCircuitAlert";
import { BundleSvg } from "../../../../images/customIcons/BundleSvg";
import {
  getLocationType,
  getFilterDisplaysForContains,
  getDisplaysIdsFromMap,
  displayIsCircuit,
  displayIsMultipleFaces,
  checkIfBundle,
} from "../../../../services/display";
import Colors from "../../../../themes/Colors";
import defaultImage from "../../../../images/bg.jpg";

import { useDisplayMapList } from "../hooks/useDisplayMapList";
import TableResultsMapHolder from "../styles/tableResultsMapStyle";

// Ant Design Tag color per display format.
const FORMAT_TAG_COLOR = { outdoor: "blue", indoor: "green" };
// Placeholder rows rendered while the table loads (skeleton instead of a spinner).
const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => ({
  id: `skeleton-${i}`,
}));

// Gray placeholder shown when a screen is not a circuit / multiple-faces / bundle.
const GrayLine = () => (
  <span
    style={{
      display: "inline-block",
      width: 16,
      height: 2,
      borderRadius: 1,
      background: Colors.gray2 || "#8c8c8c",
    }}
  />
);

const AddButton = ({
  display,
  inCart,
  listBundle,
  t,
  onAdd,
  onRemove,
  onOpenBundles,
}) => {
  const hasBundles = display.bundles && display.bundles.length > 0;
  const countBundlesAdded = hasBundles
    ? checkIfBundle(display.bundles, listBundle)
    : 0;

  return (
    <>
      {inCart ? (
        <Button
          shape="circle"
          size="small"
          type="danger"
          icon="minus"
          disabled={!!display.bundle_only}
          onClick={() => onRemove(display.id)}
        />
      ) : (
        <Button
          shape="circle"
          size="small"
          type="primary"
          icon="plus"
          disabled={!!display.bundle_only}
          onClick={() => onAdd(display)}
        />
      )}
      {hasBundles ? (
        <Tooltip placement="top" title={t("Sales bundle details")}>
          <Button
            className="tableRowBundleButton"
            shape="circle"
            size="small"
            onClick={() => onOpenBundles(display)}
          >
            <Badge
              count={
                countBundlesAdded !== 0 &&
                countBundlesAdded < display.bundles.length ? (
                  <>
                    <Icon
                      type="plus-circle"
                      className="addIconBundle"
                      theme="filled"
                    />
                    <Icon
                      type="minus-circle"
                      className="minusIconBundle"
                      theme="filled"
                    />
                  </>
                ) : countBundlesAdded === display.bundles.length ? (
                  <Icon
                    type="minus-circle"
                    className="minusIconBundle"
                    theme="filled"
                  />
                ) : (
                  <Icon
                    type="plus-circle"
                    className="addIconBundle"
                    theme="filled"
                  />
                )
              }
            >
              <Icon component={BundleSvg} className="tableRowBundleIcon" />
            </Badge>
          </Button>
        </Tooltip>
      ) : null}
    </>
  );
};

const getThumb = (display) => {
  const pics = display.pictures;
  if (!pics || pics.length === 0 || !pics[0] || !pics[0].url) {
    return defaultImage;
  }
  return pics[0].url.split(".").slice(0, -1).join(".") + "-thumb.jpg";
};

const getSizeLabel = (sizeType, t) => {
  switch (sizeType) {
    case "small":
      return t("Small");
    case "medium":
      return t("Medium");
    case "big":
      return t("Big");
    case "giant":
      return t("Giant");
    default:
      return "-";
  }
};

const getPriceValue = (display, programmatic) =>
  programmatic === "programmatic" ? display.cpmi : display.price_per_day;

// Column key ↔ backend sort value (/displays/map: region|format|size|price).
const COLUMN_TO_SORT = {
  region: "region",
  location_type: "format",
  size_type: "size",
  price: "price",
};
const SORT_TO_COLUMN = {
  region: "region",
  format: "location_type",
  size: "size_type",
  price: "price",
};



function TableResultsMap({
  // data props (forwarded to the hook)
  displaysMap,
  displaysMapMode,
  displays,
  getDisplays,
  date_from,
  date_to,
  client,
  dsp,
  currency,
  programmatic,
  editCampaignActive,
  campaignCurrency,
  campaignInvoiceIssuingCountry,
  invoiceIssuingCountry,
  isGuaranteed,
  // interaction props
  user,
  cart,
  listBundle,
  fetchingDisplaysMap,
  fetchingCart,
  addDisplayToCart,
  getDisplaysCart,
  getBulkDisplaysCart,
  removeDisplayFromCart,
  removeDisplayFromCartBulk,
  sort,
  sortDir,
  onSortChange,
}) {
  const { t } = useTranslation();
  const [screenDetail, setScreenDetail] = useState(null);
  // Which tab the detail modal opens on ("info" | "bundles").
  const [detailTab, setDetailTab] = useState("info");

  const { listContentRef, loadedDisplays, loadingMore, handleListScroll } =
    useDisplayMapList({
      displaysMap,
      displaysMapMode,
      displays,
      getDisplays,
      date_from,
      date_to,
      client,
      dsp,
      currency,
      programmatic,
      editCampaignActive,
      campaignCurrency,
      campaignInvoiceIssuingCountry,
      invoiceIssuingCountry,
      isGuaranteed,
    });

  // Same visibility rule the cards use, so both views show the same screens.
  const visibleRows = useMemo(
    () => loadedDisplays.filter((d) => getFilterDisplaysForContains(d, user)),
    [loadedDisplays, user],
  );

  const cartIds = useMemo(() => new Set((cart || []).map((d) => d.id)), [cart]);
  const isInCart = useCallback((display) => cartIds.has(display.id), [cartIds]);


  const displaysIds = useMemo(
    () =>
      displaysMap && displaysMap.length
        ? getDisplaysIdsFromMap(displaysMap, displaysMapMode)
        : [],
    [displaysMap, displaysMapMode],
  );

  // "Select all" reflects/controls cart membership over every matched screen.
  const allMatchedInCart =
    displaysIds.length > 0 && displaysIds.every((id) => cartIds.has(id));
  const someMatchedInCart = displaysIds.some((id) => cartIds.has(id));

  // Request params for `getDisplaysCart`, mirroring the chunk fetch in
  // useDisplayMapList so the bulk-added screens are priced/filtered identically.
  const buildCartParams = useCallback(
    (listId) => ({
      list_id: listId,
      date_from: moment(date_from || new Date()).format("YYYY-MM-DD"),
      date_to: moment(date_to || new Date()).format("YYYY-MM-DD"),
      currency: editCampaignActive ? campaignCurrency : currency,
      invoice_issuing_country: editCampaignActive
        ? campaignInvoiceIssuingCountry
        : invoiceIssuingCountry,
      client_id: client ? client.id : null,
      external_dsp_id: dsp ? dsp.id : null,
      smart_campaign_enabled: programmatic === "programmatic" ? 1 : null,
      is_campaign: editCampaignActive ? 1 : 0,
      is_guaranteed: isGuaranteed ? 1 : 0,
      // Surface the network round-trip through the shared `fetching` flag.
      fetching: true,
    }),
    [
      date_from,
      date_to,
      editCampaignActive,
      campaignCurrency,
      currency,
      campaignInvoiceIssuingCountry,
      invoiceIssuingCountry,
      client,
      dsp,
      programmatic,
      isGuaranteed,
    ],
  );

  const handleToggleSelectAll = useCallback(
    (checked) => {
      if (checked) {
       const listId = Array.from(new Set([...cartIds, ...displaysIds]));
        getBulkDisplaysCart(buildCartParams(listId));
      } else {
        const listId = displaysIds.filter((id) => cartIds.has(id));
        if (listId.length > 0) removeDisplayFromCartBulk(listId);
      }
    },
    [
      cartIds,
      displaysIds,
      getBulkDisplaysCart,
      buildCartParams,
      removeDisplayFromCartBulk,
    ],
  );

  // Controlled sort state derived from the backend params.
  const activeSortColumn = SORT_TO_COLUMN[sort] || null;
  const activeSortOrder = sortDir === "desc" ? "descend" : "ascend";

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      if (!onSortChange) return;
      const backendKey =
        sorter && sorter.order ? COLUMN_TO_SORT[sorter.columnKey] : null;
      const dir = sorter && sorter.order === "descend" ? "desc" : "asc";
      onSortChange(backendKey || null, backendKey ? dir : "asc");
    },
    [onSortChange],
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>{t("Screen")}</span>
            <span style={{ color: Colors.gray2 || "#8c8c8c" }}>|</span>
            <Checkbox
              checked={allMatchedInCart}
              indeterminate={someMatchedInCart && !allMatchedInCart}
              disabled={fetchingCart}
              onChange={(e) => handleToggleSelectAll(e.target.checked)}
            >
              {t("Select all")}
            </Checkbox>
            {fetchingCart ? <Spin size="small" /> : null}
          </div>
        ),
        dataIndex: "name",
        key: "name",
        render: (name, display) => {
          const location = [
            display.administrative_area_level_2,
            display.administrative_area_level_1,
          ]
            .filter(Boolean)
            .join(", ");
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={getThumb(display)}
                alt=""
                style={{
                  width: 48,
                  height: 36,
                  objectFit: "cover",
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
              <div>
                <div>{name}</div>
                <small style={{ color: Colors.gray2 || "#8c8c8c" }}>
                  {location}
                </small>
              </div>
            </div>
          );
        },
      },
      {
        title: t("Region"),
        key: "region",
        sorter: true,
        sortOrder: activeSortColumn === "region" ? activeSortOrder : null,
        render: (_, display) => (
          <span>
            <Icon type="environment" style={{ marginRight: 4 }} />
            {display.administrative_area_level_1 || "-"}
          </span>
        ),
      },
      {
        title: t("Format"),
        dataIndex: "location_type",
        key: "location_type",
        sorter: true,
        sortOrder: activeSortColumn === "location_type" ? activeSortOrder : null,
        render: (type) =>
          type ? (
            <Tag color={FORMAT_TAG_COLOR[type]}>{getLocationType(type, t)}</Tag>
          ) : (
            "-"
          ),
      },
      {
        title: t("Circuit"),
        key: "circuit",
        align: "center",
        render: (_, display) =>
          display.display_type === "main" && display.secondary_displays ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <CircuitButton secondaryDisplays={display.secondary_displays} />
              {display.sync_circuit ? <SyncCircuitAlert /> : null}
            </div>
          ) : (
            <GrayLine />
          ),
      },
      {
        title: t("Multiple faces screen"),
        key: "multiple_faces",
        align: "center",
        render: (_, display) =>
          displayIsMultipleFaces(display) ? (
            <MultipleFacesButton
              mirror_screens_count={display.mirror_screens_count}
            />
          ) : (
            <GrayLine />
          ),
      },
      {
        title: t("Size"),
        dataIndex: "size_type",
        key: "size_type",
        sorter: true,
        sortOrder: activeSortColumn === "size_type" ? activeSortOrder : null,
        render: (sizeType) => getSizeLabel(sizeType, t),
      },
      {
        title: t("Price"),
        key: "price",
        align: "right",
        sorter: true,
        sortOrder: activeSortColumn === "price" ? activeSortOrder : null,
        render: (_, display) => (
          <span style={{ color: Colors.primaryAccent }}>
            <NumberFormat
              value={getPriceValue(display, programmatic)}
              currency={display.price_currency}
              include_currency={true}
            />
            {programmatic === "programmatic" ? " CPM" : `/${t("Day")}`}
          </span>
        ),
      },
      {
        title: t("Actions"),
        key: "actions",
        align: "left",
        // Wide enough for the three buttons (3 × 24px + 2 × 6px gap + cell
        // padding), so the column never shrinks on rows that show all of them.
        width: 120,
        render: (_, display) => {
          const inCart = isInCart(display);
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 6,
                minWidth: 84,
                whiteSpace: "nowrap",
              }}
            >
              <Button
                shape="circle"
                size="small"
                icon="info"
                onClick={() => {
                  setDetailTab("info");
                  setScreenDetail(display);
                }}
              />
              <AddButton
                display={display}
                inCart={inCart}
                listBundle={listBundle}
                t={t}
                onAdd={addDisplayToCart}
                onRemove={removeDisplayFromCart}
                onOpenBundles={(d) => {
                  setDetailTab("bundles");
                  setScreenDetail(d);
                }}
              />
            </div>
          );
        },
      },
    ],
    [

      programmatic,
      isInCart,
      addDisplayToCart,
      removeDisplayFromCart,
      allMatchedInCart,
      someMatchedInCart,
      fetchingCart,
      handleToggleSelectAll,
      listBundle,
      activeSortColumn,
      activeSortOrder,
    ],
  );


  const showInitialLoading =
    fetchingDisplaysMap || (loadingMore && visibleRows.length === 0);

  // While loading, reuse the same columns but render a skeleton line per cell.
  const skeletonColumns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        render: () => (
          <Skeleton active paragraph={false} title={{ width: "100%" }} />
        ),
      })),
    [columns],
  );

  return (
    <>
      <TableResultsMapHolder ref={listContentRef} onScroll={handleListScroll}>
        <Table
          rowKey="id"
          columns={showInitialLoading ? skeletonColumns : columns}
          dataSource={showInitialLoading ? SKELETON_ROWS : visibleRows}
          rowClassName={(display) => (isInCart(display) ? "in-cart" : "")}
          pagination={false}
          onChange={handleTableChange}
          size="middle"
          locale={{
            emptyText: showInitialLoading ? null : (
              <Result
                icon={<Icon type="container" theme="twoTone" />}
                title={t("There are no screens here")}
              />
            ),
          }}
        />
        {loadingMore && visibleRows.length > 0 && (
          <div style={{ textAlign: "center", padding: 12 }}>
            <Spin />
          </div>
        )}
      </TableResultsMapHolder>

      <Modal
        title={t("Screen detail")}
        visible={!!screenDetail}
        onCancel={() => setScreenDetail(null)}
        footer={[
          <Button key="back" onClick={() => setScreenDetail(null)}>
            OK
          </Button>,
        ]}
        width={window.innerWidth > 768 ? "73%" : "90%"}
        style={{ top: 10 }}
      >
        {screenDetail ? (
          <Provider store={store}>
            <ScreenContainer
              display={screenDetail}
              screenDetail={!!screenDetail}
              popup={true}
              defaultTab={detailTab}
              editCart={true}
              closeModal={() => setScreenDetail(null)}
            />
          </Provider>
        ) : null}
      </Modal>
    </>
  );
}

export default React.memo(TableResultsMap);
